import React from 'react';

export const SampleContent = ({ book }) => (
  <div className="prose prose-slate max-w-none">
    <h1 className="text-4xl font-bold text-slate-900 mb-6">{book?.title || "Ned and Nancy: The First Clue"}</h1>
    <p className="text-sm text-slate-500 mb-8">by Carter Case</p>
    
    <img 
      src={book?.image || "/images/book1.jpg"}
      alt={`${book?.title || 'Book'} Cover`}
      className="w-full max-w-2xl mx-auto rounded-lg shadow-lg my-8"
    />

<div id="chapter-1"></div>
    <h2 className="text-3xl font-bold text-slate-900 mt-12 mb-6">Chapter 1: Separate Worlds</h2>
    
    <p>
      Nancy squinted against the glare of the afternoon sun, a magnifying glass held aloft in her right hand as she crouched low over the grass. Her backyard had become a tapestry of secrets to unravel, each clover and pebble a potential clue in the grand scheme of her mini-mysteries.
    </p>

    <p>
      "Another case closed," she announced to the empty yard, popping up from her hunched position over a 'stolen' cookie that lay amidst the shrubbery, its crumbly circumference now less of a mystery and more of a snack for the ants. With a satisfied nod, she scribbled the last notes into her notepad, her wavy red hair falling over her face like a curtain of determination.
    </p>

    <p>
      On the other side of the wooden fence, laughter bubbled up like a cheerful potion coming to a boil. Nancy's ears perked up, recognizing the familiar sound. She tiptoed, peeking over the fence to catch a glimpse of Ned, who was elbow-deep in a bowl of flour, his dark brown hair dusted white at the fringe.
    </p>

    <p>
      "Perfecting the art of the invisible pie, Ned?" she teased, a playful glint in her piercing green eyes.
    </p>

    <p>
      "Ha! If only it were that mystical," Ned chuckled, looking up from his latest culinary experiment. "I'm trying to create the ultimate comic book snack—fusion cuisine meets superhero fuel!"
    </p>

    <p>
      "Sounds... adventurous," Nancy replied, her voice tinged with a hint of genuine intrigue mingled with amusement.
    </p>

    <p>
      "Every hero needs their signature dish," Ned quipped, waving a whisk like a wizard brandishing his wand. He gave the batter before him an enthusiastic stir. "Today, it's 'The Flapjack Flip' versus 'The Pancake Pummel.' May the best breakfast win!"
    </p>

    <p>
      "Your dedication is commendable," Nancy said with a smile, leaning on the fence now, her detective's mind momentarily swept up in Ned's world of whimsy and wonder. She imagined herself, not just a backyard detective, but a character in one of those vivid panels, solving crimes and munching on Ned's quirky creations.
    </p>

    <p>
      "Speaking of dedication," Ned said, eying the notepad in her hands. "Cracked another backyard caper, have you?"
    </p>

    <p>
      "Indeed," Nancy replied, tucking her notepad away. "It seems the Case of the Missing Sweets was merely a picnic pilfered by our local wildlife."
    </p>

    <p>
      "Those crafty critters," Ned laughed, shaking his head. "They're the real super villains around here."
    </p>

    <p>
      "Perhaps," Nancy mused, her mind already racing with the thrill of her next case, "but every villain has an Achilles' heel. It's just a matter of finding it."
    </p>

    <p>
      "Speaking of heels," Ned said, glancing down at the mess on the ground where a blob of batter had escaped, "I've got my work cut out for me if I want to save these pancakes from certain doom."
    </p>

    <p>
      "Good luck, Ned," Nancy called over her shoulder as she headed back to her own domain. "You'll need it."
    </p>

    <p>
      "Thanks!" Ned shouted back, his grin audible. "I can't wait for summer—endless days for experiments and comics!"
    </p>

    <p>
      Little did he know, the upcoming school project would soon intertwine their worlds in ways beyond even his imaginative grasp. But for now, Nancy delved back into her realm of riddles and clues, while Ned lost himself to the joyous sizzle of batter meeting pan, both content in their separate spheres of mysteries and marvels.
    </p>

    <p>
      The school bell echoed through the hallways, signaling the end of another day. Nancy, with her canvas backpack sagging under the weight of detective novels, stepped into the bustling torrent of students with an air of detachment. Her piercing green eyes swept the scene, a silent observer cataloging the myriad interactions around her: secretive whispers between lockers, the scuff of sneakers on linoleum, and the melodic laughter that rose like bubbles from a soda pop.
    </p>

    <p>
      "Did you see Mr. Clayton's tie today?" Ned's voice cut through the din as he sidled up to Nancy, his own bag adorned with patches of superheroes and culinary insignia. "I swear it had more stripes than my Aunt Mabel's cat has lives!"
    </p>

    <p>
      Nancy smiled faintly, appreciating Ned's knack for light-hearted commentary. "It was certainly... vibrant," she conceded, recalling the neon monstrosity in question. Their pace matched each other's as they navigated the crowded hallway, Nancy weaving gracefully like a quiet stream while Ned bobbed alongside her, a buoyant and chatty presence.
    </p>

    <p>
      "Hey, Nancy," he continued, juggling a comic book in one hand and a half-eaten granola bar in the other. "What's your take on the cafeteria mystery meat? Alien substance or culinary experiment gone wrong?"
    </p>

    <p>
      She laughed softly, a sound as rare and delicate as the flutter of moth wings. "Perhaps a bit of both," Nancy responded, her mind never far from intrigue and enigma. "But I haven't solved that mystery yet."
    </p>

    <p>
      "Ah, well, when you do, let me know," Ned grinned, brushing crumbs off his shirt. "Might be the key to my next snack creation!"
    </p>

    <p>
      As they stepped out of the school's front doors, the sunlight felt like a spotlight on Nancy's yearning. She paused for a moment, soaking in the warmth on her freckled face, letting her thoughts drift to the longing that whispered through her veins like an unsolved case calling out to her.
    </p>

    <p>
      "Hey, earth to Nancy!" Ned waved a hand in front of her face. "You're doing the thing again—where you go all Sherlock on us and fade away into Nancy-land."
    </p>

    <p>
      "Sorry," she said, blinking back to reality. "Sometimes I just wish for an adventure that's... bigger than backyard mysteries, you know?"
    </p>

    <p>
      "Like finding lost treasure or busting an international smuggling ring?" Ned asked, his imagination already catching fire.
    </p>

    <p>
      "Exactly," Nancy breathed, the word rolling out like a secret promise. "Something that would make even the great detectives in my books tip their hats."
    </p>

    <p>
      "Count me in!" Ned declared with gusto. "Ned Nickerson, at your service. Sidekick, comic relief, and provider of all necessary snacks!"
    </p>

    <p>
      Nancy's lips curled into a genuine smile at Ned's enthusiasm. "We'll see, Ned. For now, let's just try to survive Mrs. Abbott's history test tomorrow."
    </p>

    <p>
      "Right," Ned agreed with mock solemnity, puffing out his chest. "To the library! For knowledge, justice, and the right to pass seventh grade!"
    </p>

    <p>
      And with her heart alight with dreams of grandeur and the gentle pull of camaraderie tugging at her sleeve, Nancy strode forward, the idea of a real adventure shimmering on the horizon of her imagination like a mirage that promised to become reality.
    </p>

    <p>
      The school bell clanged with the finality of a gavel, heralding the end of another monotonous day. Ned stuffed his science textbook into his backpack with less care than one might give a crumpled napkin, his mind already racing ahead to the boundless possibilities of summer.
    </p>

    <p>
      "Only two weeks left," he mused aloud, flashing a grin at the empty hallway. His glasses caught the fluorescent light as he pushed them up the bridge of his nose.
    </p>

    <p>
      "Freedom from the tyranny of algebra!" He executed a little spin, his backpack swinging in a wide arc. "Endless days filled with the three C's: Comics, Cooking, and...um..." He faltered for a moment, searching for a third 'C' before snapping his fingers triumphantly, "Chilling!"
    </p>

    <p>
      Ned navigated the sea of lockers, his thoughts drifting like dandelion fluff. He imagined himself lounging under the forgiving shade of the old oak tree in his backyard, a tower of comic books beside him, and a homemade cookie in hand—the kind that crumbled just right.
    </p>

    <p>
      "Summer is the perfect time to test out my new recipes," he pondered, his tongue practically tingling with the phantom taste of experimental s'mores—extra chocolate, no doubt. "Maybe I'll invent the next big snack sensation! The Nedlicious Bar or something."
    </p>

    <p>
      He chuckled, liking the sound of it. His chuckle was a solitary sound in the echo of departing footsteps, but it was warm and it carried dreams.
    </p>

    <p>
      "Hey Ned!" A voice sliced through his reverie. It was Nancy, her face thoughtful, almost pensive, as if she solved life's enigmas with each step she took.
    </p>
    
    <p>
      "Ready for summer?" he asked, expecting an echo of his own excitement.
    </p>

    <p>
      "Absolutely," she replied, though her gaze seemed hitched to something distant, something stirring beyond the mundane cycle of seasons. "But I've got this feeling something unexpected is coming our way."
    </p>

    <p>
      "Unexpected like a pop quiz in gym class?" Ned joked, trying to draw her back to the simple joys of impending summer days.
    </p>

    <p>
      "Maybe," she said, a cryptic smile playing on her lips. "Just be prepared for anything."
    </p>

    <p>
      "Preparedness," Ned declared with an air of mock formality, "is my middle name."
    </p>

    <p>
      "Since when?" Nancy teased, her eyes twinkling.
    </p>

    <p>
      "Since always," Ned asserted, puffing up his chest. "I'm always ready for a good snack or a good laugh."
    </p>

    <p>
      They reached the school's exit, students spilling out like ants from an upturned hill. Ned felt the sun's embrace, a prelude to his upcoming vacation—a vacation he was certain would be filled with laziness and culinary quests.
    </p>

    <p>
      "Whatever summer brings, we'll make it epic," he proclaimed, unaware that the universe often has its own designs, hidden in the fine print of tomorrow's plans. For now, Ned simply basked in the golden glow of ignorance, his heart set on the leisurely horizon, his spirit untethered by the weight of any looming school project.
    </p>

    <p>
      "Bring it on, summer," he whispered to himself. "I can't wait to see what you've got in store for me."
    </p>

    <div id="chapter-2"></div>
    <h2 className="text-3xl font-bold text-slate-900 mt-16 mb-6">Chapter 2: An Unlikely Pair</h2>

    <p>
      The final bell of the school year let out a jubilant ring, sending waves of cheers through the corridors. It was a sound that seemed to unfurl like a victory flag, proclaiming the end of math quizzes and book reports. But in Mrs. Peterson's history class, the celebration was cut short by an unexpected announcement.
    </p>

    <p>
      "Before you scatter like marbles on a tile floor," Mrs. Peterson called over the din, her voice a lasso pulling every ear to attention, "I have one last piece of news. This summer, your brains won't be gathering dust like forgotten attic trunks. You'll be paired up for a local history project!"
    </p>

    <p>
      Nancy's piercing green eyes narrowed slightly. The thought of homework during summer cast a shadow over the sunny prospects of unstructured days filled with mystery novels and puzzle-solving. She chewed thoughtfully on a strand of her wavy red hair, her mind already ticking away at the possibilities, secretly thrilled by the challenge, yet outwardly betraying nothing but annoyance.
    </p>

    <p>
      "Partners will be... Nancy and Ned!" Mrs. Peterson announced, peering over her glasses with a smile that suggested she knew something they didn't.
    </p>

    <p>
      "Aw, history?" Ned groaned from the desk beside her, his lanky frame slumping like a deflated balloon. His straight dark brown hair flopped into his eyes, which were magnified by the round lenses perched on his nose. He brushed the hair aside and reached for a half-eaten bag of pretzels in his backpack. "And with Nancy? No offense, but you're kinda intense about this stuff."
    </p>

    <p>
      "Intense?" Nancy sniffed, her lips pursing just enough to show her displeasure. "I prefer 'dedicated.' And if we must work together, Ned, can you at least promise not to eat through every brainstorming session?"
    </p>

    <p>
      "Hey, snacks fuel creativity," he quipped, flashing a grin that showed he wasn't taking any of this too seriously. "You bring your magnifying glass or whatever detectives need, and I'll bring the brain food."
    </p>

    <p>
      She folded her arms, feeling the reluctant tug of a smile. Ned had a way of making even the gloomiest situations seem light. "Fine," she conceded. "But if we're doing this, we're doing it my way—no dusty old books and dates. We're going to solve a real piece of history."
    </p>

    <p>
      "Sounds like a detective novel," Ned chuckled, popping another pretzel into his mouth. "Count me in, Nancy Drew."
    </p>

    <p>
      "Please," she huffed with mock indignation, "I do my own stunts." But inwardly, Nancy felt a flicker of excitement. Maybe, just maybe, this would be the summer she'd unravel a true mystery, and having a sidekick like Ned could prove more entertaining than she cared to admit.
    </p>

    <p>
      Nancy's sneakers scuffed the sidewalk as she approached the Riverton Town Library, where she and Ned had agreed to meet. A warm breeze carried the scent of freshly mown grass and whispered promises of summer adventures. Her fingers drummed against her thigh—she was eager to get started, but nervous energy buzzed beneath her skin like bees in a hive.
    </p>

    <p>
      "Hey, Nancy!" Ned called out from the library steps, waving a comic book in one hand and a half-eaten chocolate bar in the other. "Ready to make history? Or, well, study it?"
    </p>

    <p>
      "Hi, Ned," she replied, ascending the steps while eyeing his choice of snack. "I hope you brought more than just candy bars to this meeting."
    </p>

    <p>
      "Got pretzels too!" he said with a cheeky grin, patting his bulging backpack.
    </p>

    <p>
      "Great," Nancy muttered under her breath. She took a deep breath, preparing for the clash of ideas that was sure to come. "Let's get inside. We need a plan."
    </p>

    <p>
      "Right behind you, boss," Ned quipped, following her into the cool, hushed interior of the library.
    </p>

    <p>
      They settled at a round table, scattered with Ned's colorful wrappers and Nancy's neat stack of notepads and pens. It was clear they were from different worlds, yet here they were, about to dive into the past together.
    </p>

    <p>
      "Okay, let's brainstorm," Nancy began, tapping her pen with authority. "We need something gripping for our project, not the usual boring facts and figures."
    </p>

    <p>
      "Agreed," Ned replied, his eyes scanning the library shelves. "But what if we made it like a... superhero origin story? You know, how Riverton came to be?"
    </p>

    <p>
      "Interesting angle," Nancy admitted, impressed despite herself. "But we need real hooks, Ned. Legends, mysteries, something that will stand out."
    </p>

    <p>
      "Legends, huh?" Ned's eyes lit up. "What about the old town museum? Mrs. Leary always talks about weird artifacts and stuff when we go on school trips."
    </p>

    <p>
      "Mrs. Leary..." Nancy mused, her mind whirring with possibilities. The museum could be a treasure trove of untapped stories. "That's actually not a bad idea. Let's do it."
    </p>

    <p>
      "Sweet!" Ned exclaimed, folding his comic book and tucking it away. He stood up, brushing crumbs from his shirt. "To the museum, then?"
    </p>

    <p>
      Nancy nodded, feeling a smile tug at the corners of her mouth. "Yes, to the museum. And Ned?"
    </p>

    <p>
      "Yeah?"
    </p>

    <p>
      "Try to keep the snacking to a minimum, okay?"
    </p>

    <p>
      "Can't make any promises," he winked, and Nancy shook her head, her smile now fully formed. This partnership might just work after all.
    </p>

    <p>
      The Riverton Town Museum was one of those places that seemed eternally stuck between a musty attic and an adventurer's treasure trove. Nancy's sneakers squeaked on the polished hardwood floors as she and Ned stepped into the cool, dimly lit main hall.
    </p>

    <p>
      "Feels like we're stepping back in time," Ned whispered, his eyes wide with a childlike wonder that made Nancy roll her own.
    </p>

    <p>
      "Or into a crypt," she quipped, though she couldn't deny the electric tingle of anticipation running through her veins.
    </p>

    <p>
      "Check this out!" Ned exclaimed, darting toward a glass case filled with sepia-toned photographs and yellowed documents. His fingers drummed against the glass, leaving fleeting impressions that fogged up and disappeared. "These are ancient!"
    </p>

    <p>
      "Try 'historical,' Ned. We're not looking at dinosaur bones." But Nancy was already moving closer, drawn by the faces staring back at them from a century ago.
    </p>

    <p>
      One photograph, in particular, seized their attention—a black and white image of a figure shrouded in shadow, standing before what appeared to be an old well. The face was indistinct, but the posture suggested someone of importance—or perhaps infamy.
    </p>

    <p>
      "Whoa, this dude looks mysterious," Ned murmured, squinting. "And check out that hat. It's like something out of a magician's closet."
    </p>

    <p>
      "Shhh," Nancy hushed him, her gaze locked onto the caption fading beneath the photo: 'The Watcher of Wishing Well Lane.'
    </p>

    <p>
      "Watcher? That sounds like superhero stuff!" Ned bounced slightly on the balls of his feet, almost knocking over a nearby display of arrowheads.
    </p>

    <p>
      "More like vigilante tales," Nancy corrected, her mind racing with questions. Who was this Watcher? And why Wishing Well Lane?
    </p>

    <p>
      "Imagine if he had powers," Ned continued, oblivious to her inner musings. "Or maybe he was a protector of the town! This could be our hook, Nancy!"
    </p>

    <p>
      She considered it, her lips pursed in thought. A story tied to a local legend would certainly captivate their classmates. And solving a piece of Riverton history? That was the kind of challenge Nancy lived for.
    </p>

    <p>
      "Okay, Ned, you might be onto something," she admitted, tapping the photo. Her reflection stared back at her—a girl poised to unravel the secrets of the past. "But we'll need to do some digging. The museum archives, perhaps?"
    </p>

    <p>
      "Archives?" Ned repeated with a groan, visions of dusty tomes and endless reading material clearly flashing through his mind. "Can we snack first?"
    </p>

    <p>
      "Fine," Nancy conceded with an eye roll. "But then we work. No more distractions."
    </p>

    <p>
      "Deal!" Ned grinned, offering a high-five that Nancy returned with only slight reluctance.
    </p>

    <p>
      "Let's unearth the story of The Watcher," she said, determination settling in her stomach like a stone. The thrill of the chase was on.
    </p>

    <p>
      "Right behind you, fearless leader," Ned chuckled, grabbing a handful of pretzels from his pocket and accidentally scattering a few across the floor. "Oops."
    </p>

    <p>
      "Seriously, Ned?" Nancy sighed, unable to suppress a smile.
    </p>

    <p>
      "Hey, I'm providing snacks for the museum mice," he said with a mischievous wink. Nancy shook her head, her spirits lifted by his incorrigible cheerfulness.
    </p>

    <p>
      They were quite the pair, she mused: Ned with his bottomless pockets full of treats, and herself with her insatiable hunger for clues. Together, they'd uncover the tale of The Watcher, piece by mysterious piece.
    </p>

    {/* Chapter Navigation and Progress */}
    <div className="mt-12 pt-8 border-t border-slate-200">
      <div className="flex justify-between items-center">
        <span className="text-sm text-slate-500">End of Chapter 2</span>
        <div className="flex items-center gap-2">
          <span className="text-sm text-slate-500">Reading Progress: 2/12 Chapters</span>
          <div className="w-32 h-2 bg-slate-200 rounded-full">
            <div className="w-1/6 h-full bg-blue-500 rounded-full"></div>
          </div>
        </div>
      </div>
    </div>
  </div>
);

export default SampleContent;